/**
 * La invitación a suscribirse.
 *
 * Aparece cuando el autor pide algo que necesita suscripción — no al abrir el panel. Ese orden es
 * la decisión: primero abre su libro, ve sus medidas y su bitácora, y solo cuando quiere encargar
 * algo aparece esto. Un panel vacío con un botón de pagar no convence a nadie; uno que ya te ha
 * enseñado tu propio libro, sí.
 *
 * El enlace lleva el identificador de ESTA instalación, así que al volver de pagar el panel se
 * abre solo. No hay clave que copiar de ningún correo: ese paso es donde se pierde la gente.
 */
import { useEffect, useState } from "react";
import Traer from "./Traer";
import { suscripcion } from "../api";
import type { Suscripcion } from "../api";

export default function Suscribir(
  { motivo, alActivarse, alCerrar }: {
    motivo?: string; alActivarse: () => void; alCerrar?: () => void;
  },
) {
  const [s, setS] = useState<Suscripcion | null>(null);
  const [esperando, setEsperando] = useState(false);

  useEffect(() => { suscripcion().then(setS).catch(() => {}); }, []);

  // Tras abrir el pago, se pregunta cada pocos segundos: el autor vuelve a esta pestaña y el
  // panel ya está abierto, sin tocar nada.
  useEffect(() => {
    if (!esperando) return;
    const t = setInterval(async () => {
      const nueva = await suscripcion().catch(() => null);
      if (nueva?.activa) { clearInterval(t); alActivarse(); }
      if (nueva) setS(nueva);
    }, 4000);
    return () => clearInterval(t);
  }, [esperando, alActivarse]);

  // ── Ya paga ──────────────────────────────────────────────────────────────────────────────
  //
  // Esta rama existe porque el menú de la cuenta promete «Suscripción y facturas» y llevaba aquí,
  // a la pantalla de «te hace falta suscribirte». Al que ya paga le estábamos pidiendo que pagara
  // otra vez, que es la peor cosa que le puedes enseñar a un cliente al corriente.
  //
  // Todo lo que se puede hacer con el dinero —cambiar la tarjeta, ver las facturas, darse de
  // baja— vive en el portal de Stripe y no aquí. No copiamos ese panel: sería mantener una
  // segunda versión peor de algo que Stripe ya hace bien, y cada campo que copiáramos sería un
  // sitio más donde tocar datos de pago. Nosotros solo abrimos la puerta.
  // El Studio gratis (Rais, 29-sep-2026): no hay nada que pagar aquí, y se dice sin rodeos.
  if (s?.activa && s.plan === "gratis") {
    return (
      <section className="carta">
        <h2>HumanInk Studio es gratis<span className="sub">sin suscripción</span></h2>
        <p className="vacio">
          Lo usas entero, sin pagarnos nada. Lo único que cuesta es lo que gastes en tu cuenta de OpenRouter,
          con tu propia llave, y el Studio te dice cuánto antes de cada encargo.
        </p>
        {alCerrar && <div className="dir"><button className="btn texto" onClick={alCerrar}>Entendido</button></div>}
      </section>
    );
  }
  if (s?.activa) {
    const fecha = s.hasta
      ? new Date(s.hasta).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })
      : null;
    const seVa = s.estado === "hasta_fin";
    return (
      <section className="carta">
        <h2>Tu suscripción<span className="sub">{s.plan ?? "activa"}</span></h2>

        <p className="vacio">
          {seVa
            ? <>Pediste la baja, así que no se renovará. <b>Sigue siendo tuya{fecha ? ` hasta el ${fecha}` : ""}</b>:
                pagaste ese periodo y se respeta entero. Si cambias de idea, se reactiva desde el portal.</>
            : <>Está al día{fecha ? <> y se renueva el <b>{fecha}</b></> : null}.</>}
        </p>

        {s.portal ? (
          <>
            <div className="dir">
              <a className="btn" href={s.portal} target="_blank" rel="noopener">
                {seVa ? "Reactivar o ver mis facturas" : "Gestionar mi suscripción"}
              </a>
            </div>
            <p className="vacio">
              Se abre el portal de Stripe: facturas, tarjeta, cambio de plan y baja. Te pedirá tu
              email y te mandará un código — es Stripe quien lo comprueba, no nosotros, y por eso
              tus datos de pago no pasan nunca por aquí.
            </p>
          </>
        ) : (
          <p className="vacio">
            El portal de facturación todavía no está abierto. Para una factura o una baja, escribe
            a <a href="mailto:info@humanink.io">info@humanink.io</a> y se hace a mano.
          </p>
        )}

        {alCerrar && (
          <div className="dir"><button className="btn fantasma" onClick={alCerrar}>Volver</button></div>
        )}
      </section>
    );
  }

  // Se llevó la licencia a otro ordenador. Decirlo con esas palabras evita el único desenlace
  // malo: que crea que ha perdido lo que pagó y escriba a soporte convencido de que le hemos
  // apagado el producto.
  if (s?.estado === "movida") {
    return (
      <section className="carta">
        <h2>Tu licencia está en otro ordenador<span className="sub">una a la vez</span></h2>
        <p className="vacio">
          La activaste en otro equipo, así que aquí quedó apagada. No has perdido nada: puedes
          traerla de vuelta cuando quieras, y entonces será el otro el que quede a la espera.
        </p>
        <Traer alEntrar={alActivarse} />
        {alCerrar && (
          <div className="dir"><button className="btn fantasma" onClick={alCerrar}>Ahora no</button></div>
        )}
      </section>
    );
  }

  // Tiene licencia de otro producto (Word o Cowork) pero no suscripción de Studio.
  //
  // Esta pantalla decía «no tienes que suscribirte a nada», y desde el 1-sep-2026 eso es falso: el
  // Studio va por su cuenta. Decirlo mal aquí sería lo peor de los dos mundos — el autor cree que
  // lo tiene, prueba a encargar algo, y se topa con una puerta que nadie le había anunciado.
  /**
   * ¿Es un cliente de los de antes?
   *
   * Esto ERA una rama que retornaba aquí mismo, con «tu licencia sigue intacta» y un solo botón:
   * «Tráelo aquí». Y ese botón únicamente funciona si su email está cargado a mano en el censo;
   * si no, lee «Ese email no tiene nada a su nombre todavía» y se acabó. Como la pantalla
   * retornaba antes de pintar los enlaces de Stripe, el cliente de siempre —que es justo el
   * público de este lanzamiento— se quedaba sin poder usar lo que pagó, porque la puerta le pide
   * suscripción, y sin poder pagar lo nuevo, porque no se le enseñaba dónde.
   *
   * Ahora es una introducción, no un desvío: se le explica lo suyo, se le ofrece el año, y debajo
   * están los botones como a todo el mundo. Dos salidas visibles en vez de un callejón.
   */
  const yaCliente = !!(s?.licencia.activa && s.licencia.perpetua);


  /**
   * El email, puesto DESDE AQUÍ y no desde el servidor.
   *
   * Stripe acepta `prefilled_email` en el enlace de pago, y quien ya compró una licencia de Word
   * tiene su correo guardado en este ordenador. Rellenárselo le ahorra teclear y, sobre todo,
   * hace que su cliente de Stripe y su licencia lleven el mismo email — que es lo que después
   * permite reconocerle cuando escriba a soporte.
   *
   * Se compone en el navegador del autor a propósito. Si el correo viajara a nuestro worker para
   * que él armara el enlace, habríamos empezado a recoger datos personales de nuestros clientes
   * para ahorrarles un campo de formulario, y la promesa es que a nosotros solo nos llegan
   * medidas. Aquí el dato no sale de su máquina hasta que él mismo pulsa y va a Stripe.
   */
  const correo = s?.licencia.correo;
  const conCorreo = (u: string | null | undefined) =>
    u && correo ? `${u}&prefilled_email=${encodeURIComponent(correo)}` : u;

  const enlaces = s?.enlaces;
  const hayDonde = !!(enlaces?.mensual || enlaces?.anual);

  return (
    <section className="carta">
      {yaCliente && (
        <>
          <h2>Tu licencia sigue intacta<span className="sub">{s?.licencia.tier}</span></h2>
          <p className="vacio">
            Compraste una licencia de por vida y no se toca: lo que pagaste es tuyo y seguirá
            funcionando. <b>El Studio va aparte</b>, con su propia suscripción.
          </p>
          <p className="vacio">
            Si ya eras cliente antes de que existiera, <b>puede que tengas un año esperándote</b>:
            compruébalo aquí con el email de tu compra. Y si no aparece, abajo tienes la
            suscripción normal — escríbenos y lo miramos.
          </p>
          <Traer alEntrar={alActivarse} />
          <hr className="corte" />
        </>
      )}
      <h2>Para esto hace falta una suscripción<span className="sub">{s?.plan ?? "sin suscripción"}</span></h2>

      <p className="vacio">
        {motivo || "Encargar trabajo a un colaborador y escribir en tu manuscrito necesitan suscripción."}
        {" "}Mirar tu libro, la bitácora y las medidas no: eso es tuyo y sigue abierto.
      </p>

      <p className="vacio">
        El consumo de IA no va aquí: lo pagas tú directamente a tu proveedor con tu propia llave.
        Esto es la herramienta.
      </p>

      {hayDonde ? (
        <>
          <div className="dir">
            {enlaces?.mensual && (
              <a className="btn" href={conCorreo(enlaces.mensual)!} target="_blank" rel="noopener"
                 onClick={() => setEsperando(true)}>Suscribirme al mes</a>
            )}
            {enlaces?.anual && (
              <a className="btn fantasma" href={conCorreo(enlaces.anual)!} target="_blank" rel="noopener"
                 onClick={() => setEsperando(true)}>Un año, más barato</a>
            )}
          </div>
          <p className="vacio">
            {esperando
              ? "Esperando a que el pago llegue… puedes volver aquí cuando termines; esto se abre solo."
              : "Se paga en Stripe y el panel se desbloquea solo al volver. No hay clave que copiar."}
          </p>
        </>
      ) : (
        <p className="vacio">
          {s?.sinRed
            ? "No he podido consultar tu suscripción: sin conexión. Vuelve a intentarlo en un momento."
            : "Todavía no se puede pagar desde aquí. Escribe a info@humanink.io y te damos acceso."}
        </p>
      )}

      {/* Y para quien ya tiene algo a su nombre: el año regalado del Club, o una suscripción que
          se quedó en otro ordenador. Va debajo del pago, no encima: es el caso menos común.
          Al cliente de siempre ya se le ha ofrecido arriba, así que aquí no se repite: dos
          formularios idénticos en la misma pantalla se leen como un fallo. */}
      {!yaCliente && <Traer alEntrar={alActivarse} />}

      {alCerrar && (
        <div className="dir"><button className="btn fantasma" onClick={alCerrar}>Ahora no</button></div>
      )}
    </section>
  );
}
